import { useState } from "react";
import { MapPinOff, Save, X } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../services/api";
import AddressFields from "../shared/AddressFields";
import { Button } from "../ui/button";

const hasAddress = (job) => !!((job?.street || "").trim() || (job?.address || "").trim());

// Shown to PMs/office/admin on active jobs with no address so every site gets geocoded.
const MissingAddressPrompt = ({ job, canManageJobs, onSaved }) => {
  const key = `v2r_addr_prompt_dismissed_${job?.id}`;
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(key) === "1");
  const [form, setForm] = useState({ street: "", apt: "", city: "", state: "", zip_code: "" });
  const [saving, setSaving] = useState(false);

  if (!job || !canManageJobs || job.is_system_job || dismissed || hasAddress(job)) return null;

  const dismiss = () => { sessionStorage.setItem(key, "1"); setDismissed(true); };

  const save = async () => {
    if (!form.street.trim()) { toast.error("Enter at least a street address"); return; }
    setSaving(true);
    try {
      const res = await apiClient.put(`/jobs/${job.id}`, form);
      toast.success("Address saved — jobsite will be located for foreman check-ins");
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="missing-address-prompt">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 text-amber-900">
          <MapPinOff className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">This job has no jobsite address</p>
            <p className="text-sm text-amber-800">Add it so foreman location check-ins and directions work for this site.</p>
          </div>
        </div>
        <button type="button" onClick={dismiss} className="text-amber-700 hover:text-amber-900" aria-label="Dismiss" data-testid="missing-address-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
      <AddressFields values={form} onChange={(f, v) => setForm((p) => ({ ...p, [f]: v }))} onChangeMany={(patch) => setForm((p) => ({ ...p, ...patch }))}
        prefix="prompt" title="" showBorder={false} compact />
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} data-testid="missing-address-save">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Address"}
        </Button>
      </div>
    </div>
  );
};

export default MissingAddressPrompt;
